import { EyeIcon, EyeSlashIcon } from '@phosphor-icons/react'
import { useId, useState } from 'react'

import { cn } from '@/lib/cn'
import { haptic } from '@/lib/haptics'

/**
 * Campo de las pantallas de acceso.
 *
 * Etiqueta flotante, y es una decisión, no un adorno.
 *
 * La referencia que sigue este diseño usa solo marcador de posición: se ve
 * limpísimo y deja el campo sin identificar en cuanto se escribe la primera
 * letra —al revisar antes de enviar, ya no se sabe cuál era cuál—. La regla de
 * la app es al revés: etiqueta siempre visible. La etiqueta flotante cumple las
 * dos: parte como marcador y sube a rótulo pequeño al escribir.
 *
 * El relleno superior del campo está reservado desde el principio, así que el
 * texto no se mueve cuando la etiqueta sube: lo que se mueve es solo la
 * etiqueta.
 */
export function CampoAuth({
  label,
  name,
  type = 'text',
  autoComplete,
  inputMode,
  errores,
  ayuda,
}: {
  label: string
  name: string
  type?: 'text' | 'email' | 'password'
  autoComplete?: string
  inputMode?: 'text' | 'email'
  errores?: string[]
  ayuda?: string
}) {
  const id = useId()
  const [valor, setValor] = useState('')
  const [visible, setVisible] = useState(false)
  const conError = (errores?.length ?? 0) > 0
  const lleno = valor !== ''

  // Una contraseña que no se puede ver se escribe mal en un teclado de móvil, y
  // el segundo intento es donde la gente abandona.
  const esClave = type === 'password'
  const tipoReal = esClave && visible ? 'text' : type

  return (
    <div className="flex flex-col gap-1.5">
      <div className="relative">
        <label
          htmlFor={id}
          className={cn(
            'pointer-events-none absolute left-4 origin-left transition-all duration-150',
            lleno
              ? 'top-2 text-[11px] font-semibold tracking-[0.04em] text-ink-faint'
              : 'top-1/2 -translate-y-1/2 text-[15px] text-ink-faint',
          )}
        >
          {label}
        </label>
        <input
          id={id}
          name={name}
          type={tipoReal}
          autoComplete={autoComplete}
          inputMode={inputMode}
          aria-invalid={conError}
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          className={cn(
            // 16 px de cuerpo: por debajo, iOS acerca el zoom al enfocar y
            // descoloca la pantalla entera.
            'h-[60px] w-full rounded-control border bg-surface-sunken px-4 pb-2 pt-6 text-[16px] text-ink',
            'outline-none transition-colors duration-150',
            'focus:border-primary focus:bg-surface',
            conError ? 'border-expense' : 'border-transparent',
            esClave && 'pr-12',
          )}
        />
        {esClave ? (
          <button
            type="button"
            onClick={() => {
              haptic('light')
              setVisible((v) => !v)
            }}
            aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            aria-pressed={visible}
            className="absolute right-1 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full text-ink-faint active:opacity-60"
          >
            {/* El icono dice el **estado**, no la acción: ojo tachado =
                «ahora mismo está tapada». Es la misma convención que el ojo del
                titular en el tablero, y la contraria —mostrar la acción— se lee
                al revés justo cuando importa: con la contraseña a la vista se
                veía un ojo tachado encima del texto legible. */}
            {visible ? <EyeIcon size={18} /> : <EyeSlashIcon size={18} />}
          </button>
        ) : null}
      </div>

      {conError ? (
        // El error va pegado a su campo: agruparlos arriba obliga a averiguar a
        // cuál corresponde cada uno.
        <p role="alert" className="px-1 text-[12px] leading-snug text-expense-text">
          {errores?.join('. ')}
        </p>
      ) : ayuda ? (
        <p className="px-1 text-[12px] leading-snug text-ink-faint">{ayuda}</p>
      ) : null}
    </div>
  )
}

/** Botón principal de las pantallas de acceso. */
export function BotonAuth({
  children,
  enviando = false,
  variante = 'solido',
  onClick,
  type = 'submit',
}: {
  children: React.ReactNode
  enviando?: boolean
  variante?: 'solido' | 'contorno'
  onClick?: () => void
  type?: 'submit' | 'button'
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={enviando}
      className={cn(
        'flex h-[56px] w-full items-center justify-center gap-2 rounded-pill text-[15px] font-bold',
        'transition-transform duration-150 active:scale-[0.97] disabled:opacity-70',
        variante === 'solido'
          ? 'bg-accion text-on-accion'
          : 'border border-line-strong bg-surface text-ink',
      )}
    >
      {enviando ? (
        'Un momento…'
      ) : (
        <>
          {children}
          {/* Solo el sólido lleva chevrón. En el de contorno —«ya tengo
              cuenta»— sería un segundo botón pidiendo el mismo protagonismo. */}
          {variante === 'solido' ? (
            <span aria-hidden="true" className="text-[17px] font-black leading-none text-accion-acento">
              »
            </span>
          ) : null}
        </>
      )}
    </button>
  )
}
